import { keyframes, styled } from '@stitches/react';
import { useEffect, useState } from 'react';

import { type UspDetailPopupItem } from '@/components/shared/einstein/usp-popup';
import { Text } from '@/components/shared/element/text';
import { Box } from '@/components/shared/layout/box';
import { Container } from '@/components/shared/layout/container';
import { Flex } from '@/components/shared/layout/flex';
import { Image } from '@/components/shared/media/image';
import { generateBannerImageSources } from '@/components/shared/media/image/generate-sources';

interface UspPopupHeaderProps {
  item: UspDetailPopupItem;
  className?: string;
}

const AnimateFadeUp = keyframes({
  '0%': {
    transform: 'translateY(100%)',
    opacity: 0,
  },
  '100%': {
    transform: 'translateY(0)',
    opacity: 1,
  },
});

export const StyledUspPopupHeader = styled(Box, {
  position: 'relative',
  width: '$size-full',
  zIndex: '$1',
  borderTopLeftRadius: '$rounded-4',
  borderTopRightRadius: '$rounded-4',
  overflow: 'hidden',

  '& .lead': {
    transitionProperty: 'opacity',
    willChange: 'opacity',
    transitionDuration: '800ms',
    transitionTimingFunction: 'cubic-bezier(0.17,0.84,0.44,1)',
    opacity: 0,
  },

  '& .usp-header-description': {
    animationFillMode: 'both',
    animationDuration: '1s',
    animationTimingFunction: 'cubic-bezier(0.17,0.84,0.44,1)',
  },

  '&.enter': {
    '& .lead': {
      opacity: 1,
    },
    '&.first': {
      '& .usp-header-description': {
        animationDelay: '800ms',
      },
    },
    '& .usp-header-description': {
      animationName: `${AnimateFadeUp}`,
    },
  },
});

export const UspPopupHeader = ({ item, className }: UspPopupHeaderProps) => {
  const [initHeight, setInitHeight] = useState<number>(0);
  useEffect(() => {
    if (initHeight === 0) {
      setInitHeight(window.innerHeight);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StyledUspPopupHeader
      className={className}
      data-impression={item?.menu}
      data-test="usp_header"
    >
      <Image
        src={item.headerImage.desktop.src}
        alt={item.headerImage.desktop.alt}
        width={item.headerImage.desktop.width}
        height={item.headerImage.desktop.height}
        css={{
          zIndex: '$0',
          '&::before, &::after': {
            content: '""',
            position: 'absolute',
            left: '$space-0',
            width: '100%',
            pointerEvents: 'none',
          },
          '&::before': {
            top: '$space-0',
            height: 245,
            background: `linear-gradient(180deg, ${item.cardColor} 0%, rgba(0, 0, 0, 0) 100%)`,
            opacity: 0.8,
            zIndex: '$1',
          },
          '&::after': {
            bottom: '$space-0',
            height: 250,
            background:
              'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.8) 100%)',
          },
        }}
        layout="fill"
        sources={generateBannerImageSources({
          mobileSrc: item.headerImage.mobile.src,
          mobileOptions: {
            crop: '9:16',
          },
          desktopSrc: item.headerImage.desktop.src,
          desktopOptions: {
            crop: '16:9',
          },
        })}
      />
      <Flex
        css={{
          position: 'relative',
          minHeight: `${initHeight}px`,
          paddingTop: '$space-10',
          '@lg': {
            paddingTop: '$space-16',
          },
          '@xl': {
            minHeight: '100vh',
          },
        }}
        direction="column"
        justifyContent="between"
      >
        <Container maxWidth="none">
          <Container
            size="none"
            maxWidth={{ '@initial': 'xs', '@lg': 'none' }}
            css={{
              '@2xl': { px: '$space-16' },
            }}
          >
            <Text
              variant="text-white"
              as="h3"
              size="text-body-1"
              font="bold"
              css={{
                marginBottom: '$space-2',
                transition: 'opacity 800ms cubic-bezier(0.17,0.84,0.44,1)',
                willChange: 'opacity',
                maxWidth: 'calc(100% - $size-10)',
              }}
              className="lead"
              data-test="usp_header_preTitle"
            >
              {item.preTitle}
            </Text>
            <Text
              as="h2"
              size="text-h2"
              variant="text-white"
              dangerouslySetInnerHTML={{
                __html: item.title,
              }}
              wrap
              data-test="usp_header_title"
              css={{
                pr: 32 + 16, // footer gap 16 + icon size
                '@lg': {
                  pr: 48 + 16, // footer gap 16 + icon size
                },
              }}
            />
          </Container>
        </Container>
        <Container
          maxWidth={{ '@lg': 'xs' }}
          css={{
            paddingBottom: '$space-20',
            '@lg': {
              paddingLeft: '$space-0',
              paddingRight: '$space-0',
            },
            '@2xl': {
              paddingBottom: 'calc($space-20 + $space-10)',
            },
          }}
        >
          <Text
            as="div"
            size="text-body-1"
            variant="text-gray-lightest"
            dangerouslySetInnerHTML={{
              __html: item.description,
            }}
            wrap
            data-test="usp_header_description"
            className="usp-header-description"
            css={{
              textAlign: 'center',
            }}
          />
        </Container>
      </Flex>
    </StyledUspPopupHeader>
  );
};

UspPopupHeader.displayName = 'SharedUspPopupHeader';
